import {Component, DestroyRef, ElementRef, HostListener, inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl} from '@angular/forms';
import {filter, finalize, first} from 'rxjs/operators';
import {Website} from "../../../../../websites/website/website";
import {ResellerApiService} from '../../services/10web-reseller-api.service';
import {Subscription, switchMap} from 'rxjs';
import {DbResponse} from '../../../../../shared/interceptors/db-response';
import {NotifierComponent} from '../../../../../shared/components/notifier/notifier.component';
import {Notification} from '../../../../../shared/components/notifier/notification';
import {NotificationType} from '../../../../../shared/components/notifier/notification-type';
import {Product} from "../../../../../websites/website/product";
import {Store} from '@ngrx/store';
import * as fromStore from '../../../../../store';
import {DbStoreState} from '../../../../../store';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {UserSubscription, UserSubscriptionCategory} from "../../../../../user/user-subscription";
import * as tenWebApiActions from '../../../../../store/actions/10web-api.actions';

@Component({
    selector: 'db-pre-installed-plugins',
    templateUrl: './pre-installed-plugins.component.html',
    styleUrls: ['./pre-installed-plugins.component.scss'],
    standalone: false
})
export class PreInstalledPluginsComponent implements OnInit, OnDestroy {
  protected readonly UserSubscriptionCategory = UserSubscriptionCategory;
  searchTerm = new FormControl('');
  plugins: Product[] = [];
  requiredPlugins: string[] = ['tenweb-builder', 'elementor', 'tenweb-speed-optimizer'];
  filteredPlugins: any[];
  searchResults: any[] = [];
  availablePlugins: any[] = [];
  isUpdatingList = false;

  tenWebPluginsReference: Record<string, any> = {
    'tenweb-builder': {
      slug: 'tenweb-builder',
      title: '10Web Builder',
      logo: '/assets/images/logos/builder.jpg',
      author: '10Web',
      productId: 169
    },
    'elementor': {
      slug: 'elementor',
      title: 'Elementor',
      logo: '/assets/images/logos/elementor.jpg',
      author: 'Elementor',
      productId: null
    },
    'tenweb-speed-optimizer': {
      slug: 'tenweb-speed-optimizer',
      title: '10Web Booster',
      logo: 'https://ps.w.org/tenweb-speed-optimizer/assets/icon-256x256.png?rev=2671739',
      author: '10Web',
      productId: 177
    },
    'ai-assistant-by-10web': {
      slug: 'ai-assistant-by-10web',
      title: '10Web AI Assistant',
      logo: 'https://ps.w.org/ai-assistant-by-10web/assets/icon-256x256.png?rev=2837207',
      author: '10Web',
      productId: 205
    },
    'ai-assistant-by-10web-seo-pack': {
      slug: 'ai-assistant-by-10web-seo-pack',
      title: '10Web AI Assistant SEO Pack',
      logo: 'https://ps.w.org/ai-assistant-by-10web-seo-pack/assets/icon-256x256.png?rev=2939325',
      author: '10Web',
      productId: 206
    }
  };

  // Map of 10Web product IDs to slugs for easy lookup
  productIdToSlugMap: Record<number, string> = {};

  showDropdown = false;
  website: Website;
  loading = false;
  isSearching = false;
  isSaving = false;
  saveSuccess = false;
  saveError: string = null;
  currentSubscription: UserSubscription;
  private _destroyRef = inject(DestroyRef);
  private _productsRequestSubscription: Subscription;
  private subscriptions: Subscription[] = [];

  constructor(
    private _resellerApiService: ResellerApiService,
    private elementRef: ElementRef,
    private _store: Store<DbStoreState>
  ) {
  }

  ngOnInit() {
    // Initialize productIdToSlugMap for looking up 10Web plugins by product_id
    Object.entries(this.tenWebPluginsReference).forEach(([slug, plugin]) => {
      if (plugin.productId) {
        this.productIdToSlugMap[plugin.productId] = slug;
      }
    });
    this._loadSubData();
    this.setupSearch();

    const storeSub = this._store.select(fromStore.getEnhancedResellerProducts)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(result => {
        if (result === null) {
          this.updateRequiredPlugins();
          this.setupPlugins(null, null, true);
        } else {
          const {tenWebProducts, wpProducts} = result;
          this.updateRequiredPlugins();
          this.setupPlugins(tenWebProducts, wpProducts, false);
        }
      });

    this.subscriptions.push(storeSub);
  }

  ngOnDestroy() {
    if (this._productsRequestSubscription) {
      this._productsRequestSubscription.unsubscribe();
    }

    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  setupSearch() {
    const sub = this.searchTerm.valueChanges.subscribe((value) => {
      if (value && value.trim().length > 0) {
        this.isSearching = true;
        this.showDropdown = true;
        this._getWordpressPlugins();
      } else {
        this.searchResults = [];
        this.availablePlugins = [];
        this.showDropdown = false;
      }
    });

    this.subscriptions.push(sub);
  }

  onSearchInputFocus() {
    this.searchResults = [];
    this.availablePlugins = [];
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    const clickedElement = event.target as HTMLElement;
    const inputElement = this.elementRef.nativeElement.querySelector('.custom-dropdown-input');
    const dropdownElement = this.elementRef.nativeElement.querySelector('.custom-dropdown-results');

    if (inputElement && (inputElement === clickedElement || inputElement.contains(clickedElement))) {
      return;
    }

    if (dropdownElement && (dropdownElement === clickedElement || dropdownElement.contains(clickedElement))) {
      return;
    }

    this.hideDropdown();
  }

  hideDropdown() {
    this.showDropdown = false;
  }

  isPluginInMainList(slug: string): boolean {
    return this.plugins.some(p => p.slug === slug);
  }

  choosePlugin(plugin) {
    const existingIndex = this.plugins.findIndex(p => p.slug === plugin.slug);

    if (existingIndex === -1) {
      const newPlugin = {
        ...plugin,
        selected: true,
      };

      this.plugins.push(newPlugin);
      this.filteredPlugins = [...this.plugins];
    } else {
      this.plugins[existingIndex].selected = true;
      this.filteredPlugins = [...this.plugins];
    }

    this.searchTerm.setValue('');
    this.hideDropdown();
  }

  togglePluginSelection(plugin: any): void {
    // For Booster (tenweb-speed-optimizer), allow toggling for agency users
    if (plugin.slug === 'tenweb-speed-optimizer' &&
      (this.currentSubscription?.isAgency || this.currentSubscription?.boosterCategory === UserSubscriptionCategory.AGENCY) &&
        !this.currentSubscription?.isApiResellerPlan) {
      plugin.selected = !plugin.selected;
      return;
    }

    // Only allow toggling for 10Web Builder and Elementor
    if (plugin.slug === 'tenweb-builder' || plugin.slug === 'elementor') {
      plugin.selected = !plugin.selected;
    } else if (this.requiredPlugins.includes(plugin.slug)) {
      // Don't allow deselecting other required plugins
      plugin.selected = true;
    } else {
      // Allow toggling for non-required plugins
      plugin.selected = !plugin.selected;
    }
  }

  deletePlugin(plugin: any): void {
    if (this.requiredPlugins.includes(plugin.slug)) {
      return;
    }

    this.plugins = this.plugins.filter(p => p.slug !== plugin.slug);
    this.filteredPlugins = [...this.plugins];
  }

  savePlugins(): void {
    if (this.currentSubscription?.isAgency || this.currentSubscription?.boosterCategory === UserSubscriptionCategory.AGENCY || this.currentSubscription?.isApiResellerPlan) {
      this.isSaving = true;
      this.isUpdatingList = true;
      this.saveSuccess = false;
      this.saveError = null;

      // This ensures deselected plugins are not included, even if they're in requiredPlugins
      const selectedPlugins = this.plugins.filter(p => p.selected);

      const activePlugins = selectedPlugins.map(p => p.slug).join(',');

      const productsArray = selectedPlugins.map(p => {
        // Check if it's a 10Web plugin by looking up in tenWebPluginsReference
        const tenWebPlugin = this.tenWebPluginsReference[p.slug];
        return {
          slug: p.slug,
          ...(tenWebPlugin?.productId ? {product_id: tenWebPlugin.productId.toString()} : {})
        };
      });

      this._resellerApiService
        .savePreinstalledPlugins(activePlugins, productsArray)
        .pipe(
          first(response => !!response, null)
        )
        .subscribe(response => {
          if (response.success) {
            this.saveSuccess = true;
            this.isUpdatingList = false;

            // For immediate UI update, update the local state
            this.plugins.forEach(plugin => {
              plugin.active = plugin.selected;
            });

            this.filteredPlugins = [...this.plugins];

            this._store.dispatch(new tenWebApiActions.RefreshEnhancedPlugins());

            NotifierComponent.notify(new Notification('Plugins saved successfully', NotificationType.SUCCESS));
          } else {
            this._handleSaveError();
            this.isUpdatingList = false;
          }
          this.isSaving = false;
        });
    }
  }

  handleRequiredPlugins(processedPlugins: Product[], receivedPluginSlugs: Set<string>, forceSelected: boolean = false) {
    this.requiredPlugins.forEach(requiredSlug => {
      const pluginExists = processedPlugins.some(p => p.slug === requiredSlug);

      // Determine if plugin was active based on API response
      const wasActive = receivedPluginSlugs && receivedPluginSlugs.has(requiredSlug);

      // If plugin doesn't exist in processed list yet
      if (!pluginExists && this.tenWebPluginsReference[requiredSlug]) {
        // For 10Web Builder and Elementor, respect the active state from API or use forceSelected
        // For other required plugins, always set to selected/active
        const isSpecialPlugin = requiredSlug === 'tenweb-builder' || requiredSlug === 'elementor';

        // Special handling for Booster plugin for agency users
        const isBooster = requiredSlug === 'tenweb-speed-optimizer';
        const isAgencyBooster = isBooster && (this.currentSubscription?.isAgency || this.currentSubscription?.boosterCategory === UserSubscriptionCategory.AGENCY) && !this.currentSubscription?.isApiResellerPlan;

        // Determine if it should be selected
        let shouldBeSelected;

        if (forceSelected) {
          // When forceSelected is true, all required plugins are selected
          shouldBeSelected = true;
        } else if (receivedPluginSlugs && receivedPluginSlugs.size > 0) {
          // If we have API data, respect the active state for Builder, Elementor, and Booster for agency
          if (isSpecialPlugin || isAgencyBooster) {
            shouldBeSelected = wasActive;
          } else {
            // For other required plugins, always selected
            shouldBeSelected = true;
          }
        } else {
          // For empty arrays (no data yet):
          // - Builder and Elementor are not selected by default
          // - Booster for agency starts as selected on first load only
          // - Other required plugins are selected
          if (isSpecialPlugin) {
            shouldBeSelected = false;
          } else if (isAgencyBooster) {
            // Default to selected only on first load
            shouldBeSelected = this.plugins.length === 0;
          } else {
            shouldBeSelected = true;
          }
        }

        processedPlugins.push({
          ...this.tenWebPluginsReference[requiredSlug],
          selected: shouldBeSelected,
          active: shouldBeSelected
        } as Product);
      }
    });
  }

  private _loadSubData() {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        first(loaded => loaded),
        takeUntilDestroyed(this._destroyRef),
        switchMap(() => this._store.select(fromStore.getUserSubscription)),
        first()
      )
      .subscribe((userSubscription: UserSubscription) => {
        this.currentSubscription = userSubscription;
        this.updateRequiredPlugins();
        this._getClientDataProducts();
      });
  }

  private _getClientDataProducts() {
    this.isUpdatingList = true;

    // Use the store to get enhanced products
    this._store.select(fromStore.getClientResellerDataLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => this._store.select(fromStore.getEnhancedResellerProducts)),
        first()
      )
      .subscribe(result => {
        this.isUpdatingList = false;

        // Handle the result as in handleProductsData
        if (result === null) {
          // When null is received, show required plugins WITH checkmarks
          this.updateRequiredPlugins();
          this.setupPlugins(null, null, true);
        } else {
          // When we have data (even empty arrays), process it
          const {tenWebProducts, wpProducts} = result;
          this.updateRequiredPlugins();
          this.setupPlugins(tenWebProducts, wpProducts, false);
        }
      });
  }

  private _getWordpressPlugins() {
    if (this._productsRequestSubscription) {
      this._productsRequestSubscription.unsubscribe();
      this._productsRequestSubscription = null;
    }

    this._productsRequestSubscription = this._resellerApiService.getWordpressPlugins(this.searchTerm.value)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first(),
        finalize(() => {
          this.isSearching = false;
        })
      )
      .subscribe((response: DbResponse): void => {
        if (response.success) {
          this.availablePlugins = response.data;
          this.searchResults = response.data.map(plugin => ({
            slug: plugin.slug,
            title: plugin.title,
            author: plugin.author,
            logo: plugin.logo || `https://ps.w.org/${plugin.slug}/assets/icon-256x256.png`,
            selected: false
          }));

          // Ensure our special plugins are not shown as options if they're already in the list
          this.searchResults = this.searchResults.filter(plugin => {
            const isSpecialPlugin = plugin.slug === 'tenweb-builder' || plugin.slug === 'elementor';
            const alreadyAdded = this.isPluginInMainList(plugin.slug);
            return !(isSpecialPlugin && alreadyAdded);
          });
        } else {
          this.searchResults = [];
        }
      });
  }

  private _handleSaveError(): void {
    this.saveError = 'Failed to save plugins. Please try again.';
    NotifierComponent.notify(new Notification(this.saveError, NotificationType.DANGER));
  }

  // Update required plugins based on subscription type
  private updateRequiredPlugins() {
    // Basic required plugins for everyone
    this.requiredPlugins = ['tenweb-builder', 'elementor'];

    // Add booster only for agency users who are not API reseller plan users
    if ((this.currentSubscription?.isAgency || this.currentSubscription?.boosterCategory === UserSubscriptionCategory.AGENCY) && !this.currentSubscription?.isApiResellerPlan) {
      this.requiredPlugins.push('tenweb-speed-optimizer');
    }
  }

  // Setup plugins from API data or defaults
  private setupPlugins(tenWebProducts: any[] = [], wpProducts: any[] = [], forceSelected: boolean) {
    const processedPlugins: Product[] = [];
    const receivedSlugs = new Set<string>();

    // Process API data if available
    if (tenWebProducts || wpProducts) {
      // Track which plugins we received
      [...(tenWebProducts || []), ...(wpProducts || [])].forEach(p => {
        if (p.slug) receivedSlugs.add(p.slug);
      });

      // Process plugins and add to list
      this.processApiPlugins(processedPlugins, tenWebProducts, wpProducts);
    }

    // Add required plugins if they're not already in the list
    this.handleRequiredPlugins(processedPlugins, receivedSlugs, forceSelected);

    // Remove duplicates
    this.plugins = processedPlugins.filter((plugin, index, self) =>
      index === self.findIndex(p => p.slug === plugin.slug)
    );

    // Sort plugins with all required plugins at the beginning
    this.plugins.sort((a, b) => {
      // First check if both are required or not
      const aRequired = this.requiredPlugins.includes(a.slug);
      const bRequired = this.requiredPlugins.includes(b.slug);

      // If one is required and the other isn't, the required one comes first
      if (aRequired && !bRequired) return -1;
      if (!aRequired && bRequired) return 1;

      // If both are required, sort by their position in the requiredPlugins array
      if (aRequired && bRequired) {
        return this.requiredPlugins.indexOf(a.slug) - this.requiredPlugins.indexOf(b.slug);
      }

      // For non-required plugins, keep original order
      return 0;
    });

    this.filteredPlugins = [...this.plugins];
  }

  // Process API plugins
  private processApiPlugins(processedPlugins: Product[], tenWebProducts: any[] = [], wpProducts: any[] = []) {
    // Process 10Web products
    (tenWebProducts || []).forEach(product => {
      // Known 10Web plugin by product_id
      if (product.product_id && this.productIdToSlugMap[product.product_id]) {
        const slug = this.productIdToSlugMap[product.product_id];
        const pluginInfo = this.tenWebPluginsReference[slug];

        if (pluginInfo) {
          processedPlugins.push({
            ...pluginInfo,
            selected: !!product.active,
            active: !!product.active
          } as Product);
          return;
        }
      }

      // Default handling
      processedPlugins.push({
        slug: product.slug,
        title: product.title || product.slug,
        logo: product.logo || `https://ps.w.org/${product.slug}/assets/icon-256x256.png`,
        author: product.author || '',
        productId: product.product_id,
        selected: !!product.active,
        active: !!product.active
      } as Product);
    });

    // Process WP products
    (wpProducts || []).forEach(product => {
      // Known plugin from reference
      if (product.slug && this.tenWebPluginsReference[product.slug]) {
        const pluginInfo = this.tenWebPluginsReference[product.slug];
        processedPlugins.push({
          ...pluginInfo,
          selected: !!product.active,
          active: !!product.active
        } as Product);
        return;
      }

      // Unknown WordPress plugin
      processedPlugins.push({
        slug: product.slug,
        title: product.title || product.slug,
        logo: product.logo || `https://ps.w.org/${product.slug}/assets/icon-256x256.png`,
        author: product.author || '',
        productId: product.product_id,
        selected: !!product.active,
        active: !!product.active
      } as Product);
    });
  }

}
